import "./mark-attention.css";
import { CloudflareWebAuth } from "./auth";
import { PRODUCT, WEB_ROUTES } from "./config";
import {
  CloudflareMarkAttentionRepository,
  MarkAttentionRepositoryError,
  type MarkAttentionAcknowledgement,
  type MarkAttentionCategory,
  type MarkAttentionItem,
  type MarkAttentionRepository,
} from "./mark-attention";

interface MarkAttentionAuth {
  session(): Promise<{ authenticated: boolean }>;
}

const categoryLabels: Record<MarkAttentionCategory, string> = {
  blocking: "Blocking",
  action_required: "Action required",
  input_requested: "Input requested",
  attention: "For Mark’s attention",
};

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const value = document.createElement(name);
  if (className) value.className = className;
  if (text !== undefined) value.textContent = text;
  return value;
}

function sectionHeading(
  eyebrow: string,
  title: string,
  count: number,
): HTMLElement {
  const heading = element("div", "mark-attention-section-heading");
  const copy = element("div");
  copy.append(
    element("p", "eyebrow", eyebrow),
    element("h2", undefined, title),
  );
  heading.append(
    copy,
    element(
      "span",
      "mark-attention-count",
      `${count} ${count === 1 ? "item" : "items"}`,
    ),
  );
  return heading;
}

export class MarkAttentionPage {
  private items: MarkAttentionItem[] = [];
  private readonly auth: MarkAttentionAuth;
  private readonly repository: MarkAttentionRepository;

  constructor(
    private readonly root: HTMLElement,
    dependencies: {
      auth?: MarkAttentionAuth;
      repository?: MarkAttentionRepository;
    } = {},
  ) {
    this.auth = dependencies.auth ?? new CloudflareWebAuth();
    this.repository = dependencies.repository
      ?? new CloudflareMarkAttentionRepository();
  }

  async start(): Promise<void> {
    document.documentElement.dataset.surface = "mark-attention";
    document.title = `For Mark · ${PRODUCT.name}`;
    this.renderLoading();
    try {
      const session = await this.auth.session();
      if (!session.authenticated) {
        this.renderSignedOut();
        return;
      }
      this.items = await this.repository.loadThreads();
      this.renderDashboard();
    } catch (error) {
      if (
        error instanceof MarkAttentionRepositoryError
        && error.status === 403
      ) {
        this.renderOwnerOnly();
        return;
      }
      this.renderUnavailable();
    }
  }

  private renderLoading(): void {
    this.root.replaceChildren(this.gate(
      "Owner-only queue",
      "Opening your attention queue…",
      "Convoful is checking this browser before requesting any item data.",
    ));
  }

  private gate(eyebrow: string, title: string, copy: string): HTMLElement {
    const main = element("main", "gate-shell");
    main.id = "app-content";
    main.tabIndex = -1;
    const card = element("section", "gate-card");
    const mark = element("span", "brand-mark", PRODUCT.brandMark);
    mark.setAttribute("aria-hidden", "true");
    card.append(
      mark,
      element("p", "eyebrow", eyebrow),
      element("h1", undefined, title),
      element("p", "gate-copy", copy),
    );
    main.append(card);
    return main;
  }

  private renderSignedOut(): void {
    const main = this.gate(
      "Owner-only",
      "For Mark stays private.",
      "Sign in to your Convoful account, then return to /for-mark. No attention-item data was requested.",
    );
    const card = main.querySelector<HTMLElement>(".gate-card");
    const link = element("a", "primary-link gate-primary-action", "Sign in");
    link.href = WEB_ROUTES.app;
    card?.append(link);
    this.root.replaceChildren(main);
  }

  private renderOwnerOnly(): void {
    const main = this.gate(
      "Owner-only",
      "This queue belongs to Mark.",
      "This signed-in account does not have the owner capability. No attention-item data is shown.",
    );
    const card = main.querySelector<HTMLElement>(".gate-card");
    const link = element("a", "primary-link gate-primary-action", "Return to Convoful");
    link.href = WEB_ROUTES.app;
    card?.append(link);
    this.root.replaceChildren(main);
  }

  private renderUnavailable(): void {
    const main = this.gate(
      "Private queue",
      "Your queue stayed closed.",
      "Convoful could not verify a complete owner response, so no attention-item data is shown.",
    );
    const card = main.querySelector<HTMLElement>(".gate-card");
    const retry = element("button", "primary-button gate-primary-action", "Try again");
    retry.type = "button";
    retry.addEventListener("click", () => void this.start());
    card?.append(retry);
    this.root.replaceChildren(main);
  }

  private renderDashboard(
    announcement?: string,
    focusItemID?: string,
  ): void {
    const active = this.items.filter(({ state }) => state === "open");
    const blocking = active.filter(({ category }) => category === "blocking");
    const nonBlocking = active.filter(({ category }) => category !== "blocking");
    const history = this.items.filter(({ state }) => state !== "open");
    const main = element("main", "mark-attention-shell");
    main.id = "app-content";
    main.tabIndex = -1;

    const header = element("header", "mark-attention-header");
    const brand = element("a", "mark-attention-brand");
    brand.href = WEB_ROUTES.app;
    const mark = element("span", "brand-mark brand-mark-small", PRODUCT.brandMark);
    mark.setAttribute("aria-hidden", "true");
    brand.append(mark, element("span", undefined, PRODUCT.name));
    const refresh = element("button", "mark-attention-refresh", "Refresh");
    refresh.type = "button";
    refresh.addEventListener("click", () => void this.start());
    const promptHistory = element(
      "a",
      "mark-attention-prompt-history",
      "Prompt history",
    );
    promptHistory.href = WEB_ROUTES.markPrompts;
    const tools = element("div", "mark-attention-header-tools");
    tools.append(promptHistory, refresh);
    header.append(brand, tools);

    const intro = element("section", "mark-attention-intro");
    intro.append(
      element("p", "eyebrow", "Owner-only queue"),
      element("h1", undefined, "For Mark"),
      element(
        "p",
        "mark-attention-lede",
        "Discuss each request here. Your comment and the agent reply stay together on the item.",
      ),
      element(
        "p",
        "mark-attention-email-note",
        "This page is the canonical conversation. Email may notify you, but you only need to reply here.",
      ),
    );

    const liveStatus = element("p", "visually-hidden");
    liveStatus.id = "mark-attention-status";
    liveStatus.setAttribute("aria-live", "polite");
    liveStatus.setAttribute("aria-atomic", "true");

    main.append(
      header,
      intro,
      liveStatus,
      this.renderSection(
        "First",
        "Blocking",
        blocking,
        "Nothing is blocked on you.",
      ),
      this.renderSection(
        "Then",
        "Action, input & attention",
        nonBlocking,
        "No non-blocking request needs your attention.",
      ),
      this.renderSection(
        "Kept for context",
        "Recent discussions",
        history,
        "No completed discussion yet.",
      ),
    );
    this.root.replaceChildren(main);
    if (announcement) {
      requestAnimationFrame(() => {
        liveStatus.textContent = announcement;
        const cards = Array.from(
          main.querySelectorAll<HTMLElement>(".mark-attention-card"),
        );
        const nextCard = cards.find(
          ({ dataset }) => dataset.itemId === focusItemID,
        );
        const focusTarget = nextCard?.querySelector<HTMLElement>("h3")
          ?? refresh;
        focusTarget.focus();
      });
    }
  }

  private renderSection(
    eyebrow: string,
    title: string,
    items: MarkAttentionItem[],
    emptyCopy: string,
  ): HTMLElement {
    const section = element("section", "mark-attention-section");
    section.setAttribute("aria-label", title);
    section.append(sectionHeading(eyebrow, title, items.length));
    if (items.length === 0) {
      section.append(element("p", "mark-attention-empty", emptyCopy));
      return section;
    }
    const list = element("div", "mark-attention-list");
    items.forEach((item) => list.append(this.renderItem(item)));
    section.append(list);
    return section;
  }

  private renderItem(item: MarkAttentionItem): HTMLElement {
    const article = element(
      "article",
      `mark-attention-card urgency-${item.urgency}`,
    );
    article.dataset.itemId = item.id;
    const flags = element("div", "mark-attention-flags");
    flags.append(
      element(
        "span",
        `mark-attention-category category-${item.category}`,
        categoryLabels[item.category],
      ),
      element(
        "span",
        `mark-attention-urgency urgency-label-${item.urgency}`,
        item.urgency,
      ),
    );
    if (item.state !== "open") {
      flags.append(element(
        "span",
        "mark-attention-state",
        item.state === "ok" ? "OK" : item.state,
      ));
    }
    const title = element("h3", undefined, item.title);
    title.tabIndex = -1;
    article.append(
      flags,
      title,
      element("p", "mark-attention-summary", item.summary),
      this.detail("What Mark needs to do", item.requiredAction),
      this.detail("How to reply", "Add your comment below."),
      this.itemMeta(item),
    );
    if (item.evidence.length > 0) {
      const evidence = element("div", "mark-attention-evidence");
      evidence.append(element("p", "mark-attention-detail-label", "Evidence"));
      const links = element("ul");
      item.evidence.forEach((source) => {
        const listItem = element("li");
        const link = element("a", undefined, source.label);
        link.href = source.url;
        link.target = "_blank";
        link.rel = "noreferrer";
        listItem.append(link);
        links.append(listItem);
      });
      evidence.append(links);
      article.append(evidence);
    }
    article.append(this.renderConversation(item));
    if (item.state === "open") {
      const error = element("p", "mark-attention-error");
      error.setAttribute("role", "alert");
      error.hidden = true;
      const actions = element("div", "mark-attention-actions");
      actions.append(
        this.acknowledgementButton(item, "read", "Read", error),
        this.acknowledgementButton(item, "ok", "OK", error),
      );
      article.append(error, actions);
    }
    return article;
  }

  private renderConversation(item: MarkAttentionItem): HTMLElement {
    const section = element("section", "mark-attention-conversation");
    section.setAttribute("aria-label", `Discussion: ${item.title}`);
    section.append(element("h4", undefined, "Discussion"));
    if (item.earlierCommentCount > 0) {
      section.append(element(
        "p",
        "mark-attention-earlier-comments",
        `${item.earlierCommentCount.toLocaleString()} earlier ${item.earlierCommentCount === 1 ? "comment remains" : "comments remain"} stored in the private thread.`,
      ));
    }
    if (item.comments.length === 0) {
      section.append(element(
        "p",
        "mark-attention-no-comments",
        "No comments yet. Start the discussion here.",
      ));
    } else {
      const list = element("ol", "mark-attention-comments");
      for (const comment of item.comments) {
        const entry = element(
          "li",
          `mark-attention-comment author-${comment.author.kind}`,
        );
        const header = element("div", "mark-attention-comment-header");
        header.append(
          element("strong", undefined, comment.author.label),
          element(
            "time",
            undefined,
            new Intl.DateTimeFormat(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            }).format(new Date(comment.createdAt)),
          ),
        );
        const body = element("p", undefined, comment.body);
        entry.append(header, body);
        list.append(entry);
      }
      section.append(list);
      if (item.comments.at(-1)?.author.kind === "mark") {
        section.append(element(
          "p",
          "mark-attention-reply-pending",
          "Your latest comment is waiting for an agent reply.",
        ));
      }
    }
    section.append(this.commentForm(item));
    return section;
  }

  private commentForm(item: MarkAttentionItem): HTMLFormElement {
    const form = element("form", "mark-attention-comment-form");
    const label = element("label", undefined, "Your comment");
    const textarea = element("textarea", "mark-attention-comment-input");
    const textareaID = `mark-comment-${crypto.randomUUID()}`;
    textarea.id = textareaID;
    textarea.name = "comment";
    textarea.maxLength = 4000;
    textarea.rows = 4;
    textarea.required = true;
    label.htmlFor = textareaID;
    const hint = element(
      "p",
      "mark-attention-comment-hint",
      "Your comment stays attached to this item.",
    );
    const error = element("p", "mark-attention-error");
    error.setAttribute("role", "alert");
    error.hidden = true;
    const submit = element("button", "primary-button", "Post comment");
    submit.type = "submit";
    let requestID = `web-${crypto.randomUUID()}`;
    form.append(label, textarea, hint, error, submit);
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const body = textarea.value.trim();
      if (!body) {
        textarea.setCustomValidity("Write a comment before posting.");
        textarea.reportValidity();
        return;
      }
      textarea.setCustomValidity("");
      textarea.disabled = true;
      submit.disabled = true;
      submit.textContent = "Posting…";
      error.hidden = true;
      error.textContent = "";
      try {
        const result = await this.repository.comment(
          item.id,
          item.revision,
          body,
          requestID,
        );
        item.revision = result.revision;
        item.state = result.state;
        item.comments = [...item.comments, result.comment];
        item.lastActivityAt = result.comment.createdAt;
        item.updatedAt = result.comment.createdAt;
        this.renderDashboard(
          `Your comment on ${item.title} was posted.`,
          item.id,
        );
      } catch {
        error.textContent =
          "Your comment was not confirmed. It has not been cleared; try posting again.";
        error.hidden = false;
        textarea.disabled = false;
        submit.disabled = false;
        submit.textContent = "Post comment";
        requestID = requestID || `web-${crypto.randomUUID()}`;
        textarea.focus();
      }
    });
    return form;
  }

  private detail(label: string, copy: string): HTMLElement {
    const detail = element("div", "mark-attention-detail");
    detail.append(
      element("p", "mark-attention-detail-label", label),
      element("p", undefined, copy),
    );
    return detail;
  }

  private itemMeta(item: MarkAttentionItem): HTMLElement {
    const meta = element("dl", "mark-attention-meta");
    const add = (label: string, value: string) => {
      meta.append(element("dt", undefined, label), element("dd", undefined, value));
    };
    add("Owner", `${item.owner.label} · ${item.owner.email}`);
    add(
      "Last activity",
      new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(item.lastActivityAt)),
    );
    if (item.sourceTaskID) add("Task", item.sourceTaskID);
    return meta;
  }

  private acknowledgementButton(
    item: MarkAttentionItem,
    state: MarkAttentionAcknowledgement,
    label: string,
    error: HTMLElement,
  ): HTMLButtonElement {
    const button = element(
      "button",
      state === "ok" ? "primary-button" : "mark-attention-read",
      label,
    );
    button.type = "button";
    button.setAttribute("aria-label", `${label}: ${item.title}`);
    button.addEventListener("click", async () => {
      const card = button.closest<HTMLElement>(".mark-attention-card");
      const buttons = card?.querySelectorAll<HTMLButtonElement>("button") ?? [];
      buttons.forEach((candidate) => {
        candidate.disabled = true;
      });
      error.hidden = true;
      error.textContent = "";
      try {
        await this.repository.acknowledge(item.id, item.revision, state);
        item.state = state;
        this.renderDashboard(
          `${item.title} was marked ${state === "ok" ? "OK" : "read"} and kept in recent discussions.`,
          item.id,
        );
      } catch {
        error.textContent =
          "That acknowledgement was not confirmed. The item stayed in your queue.";
        error.hidden = false;
        buttons.forEach((candidate) => {
          candidate.disabled = false;
        });
      }
    });
    return button;
  }
}

export async function renderMarkAttention(root: HTMLElement): Promise<void> {
  await new MarkAttentionPage(root).start();
}
